import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePageTitle } from 'utils/pageTitle';
import AdminLayout from 'pages/_rd_dashboard/AdminLayout';
import { useAdminSubscriptions } from 'data/repositories/adminSubscriptionsRepository';
import Spinner from 'components/Spinner';
import Paginator from 'components/Paginator';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';

export const AdminSubscriptionsPage = memo(() => {
	usePageTitle('Admin | Subscriptions');
	const dispatch = useDispatch();

	const {
		items: subscriptions,
		isLoading,
		currentPage,
		lastPage,
		total,
		actions,
	} = useAdminSubscriptions();

	const [statusFilter, setStatusFilter] = useState('all');

	useEffect(() => {
		actions.fetchPaginated(1, 15, { status: statusFilter });
	}, [actions, statusFilter]);

	const handlePageChange = (pageNum) => {
		actions.fetchPaginated(pageNum, 15, { status: statusFilter });
	};

	const handleAddSubscription = () => {
		dispatch(
			openModal(MODAL_TYPES.SUBSCRIPTION_CREATE, {
				onSuccess: () => {
					// Refresh subscriptions list after successful creation
					actions.fetchPaginated(1, 15, { status: statusFilter });
				},
			}),
		);
	};

	const getStatusBadgeClass = (status) => {
		const statusMap = {
			active: 'bg-success',
			canceled: 'bg-warning',
			trialing: 'bg-info',
			past_due: 'bg-warning',
			paused: 'bg-warning',
			ended: 'bg-dark',
		};
		return `badge ${statusMap[status] || 'bg-success'}`;
	};

	return (
		<AdminLayout>
			<div className="dashboard-portfolio">
				<div className="d-flex justify-content-between align-items-center mb-4">
					<h1>Subscriptions</h1>
					<div className="d-flex align-items-center gap-3">
						<button className="btn btn-primary" onClick={handleAddSubscription}>
							<i className="fa fa-plus me-2"></i>
							Add Enterprise
						</button>
						<label className="mb-0 me-2">Status:</label>
						<select
							className="form-select form-select-sm"
							style={{ width: 'auto' }}
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
						>
							<option value="all">All</option>
							<option value="active">Active</option>
							<option value="canceled">Canceled</option>
							<option value="trialing">Trialing</option>
							<option value="past_due">Past Due</option>
						</select>
					</div>
				</div>

				{isLoading ? (
					<Spinner />
				) : (
					<div className="pb-4">
						<div className="mb-3 text-muted">
							Showing {subscriptions?.length || 0} of {total || 0} subscriptions
						</div>

						<div className="dashboard-table-wrapper">
							<table className="table table-striped table-rounded">
								<thead>
									<tr>
										<th>User</th>
										<th>Plan</th>
										<th>Status</th>
										<th>Start Date</th>
										<th>Active Until</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{subscriptions?.length > 0 ? (
										subscriptions.map((subscription) => (
											<tr key={subscription.id}>
												<td className="dashboard-username-cell">
													<div>
														<div className="fw-bold">
															{subscription.user?.name || '-'}
														</div>
														<div className="text-muted small">
															{subscription.user?.email || '-'}
														</div>
													</div>
												</td>
												<td>
													<div>
														<div className="fw-semibold">
															{subscription.plan?.name || '-'}
														</div>
														{subscription.plan?.interval && (
															<div className="text-muted small text-capitalize">
																{subscription.plan.interval}
															</div>
														)}
													</div>
												</td>
												<td>
													<span
														className={getStatusBadgeClass(
															subscription.stripe_status,
														)}
													>
														{subscription.stripe_status}
													</span>
												</td>
												<td className="dashboard-date-time-cell">
													{subscription.start_date || '-'}
												</td>
												<td className="dashboard-date-time-cell">
													{subscription.active_until || '-'}
												</td>
												<td>
													<button
														className="btn btn-sm btn-primary"
														onClick={() => {
															// TODO: View details modal or navigation
														}}
													>
														View
													</button>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="6" className="text-center text-muted py-4">
												No subscriptions found
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						{lastPage > 1 && (
							<Paginator
								currentPage={currentPage}
								totalPages={lastPage}
								setPage={handlePageChange}
							/>
						)}
					</div>
				)}
			</div>
		</AdminLayout>
	);
});
